import PhotoSwipe from '@PhotoSwipe'
import $ from 'jquery'

import placeholderIcon from '@SvgIcon/../event-image-placeholder.svg'
import collapseIcon from '@SvgIcon/minus.svg'
import expandIcon from '@SvgIcon/plus.svg'

import { externalUrl } from '../../../shared/externalUrl'
import svgIconHtml from '../../../shared/svgIconHtml'
import { transformRawEvent } from './transformRawEvent'

export default {
  name: 'ListEvent',

  props: {
    initialEvent: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      event: this.initialEvent,
      expanded: false,
      loading: true,
      hasLoaded: false,
    }
  },

  computed: {
    today() {
      return new Date()
    },

    isThisYear() {
      return this.event.dateStartMoment.isSame(this.today, 'year')
    },

    thumb() {
      if (!this.event.photo) {
        return null
      }

      const dir = this.event.photo.url.substring(0, this.event.photo.url.lastIndexOf('/'))

      return `${dir}/_thumb/${this.event.photo.filename}`
    },

    hasDetails() {
      return (
        this.event.hasTextDescription ||
        this.event.hasRichDescription ||
        this.event.contactName ||
        this.event.contactPhone ||
        this.event.contactEmail ||
        this.event.address ||
        this.event.county ||
        this.event.admission
      )
    },
  },

  methods: {
    toggle() {
      this.expanded = !this.expanded
      this.loadData()
    },

    loadData() {
      if (this.hasLoaded) {
        return
      }

      if (this.dataXhr) {
        this.dataXhr.abort()
      }

      this.loading = true

      this.dataXhr = $.ajax({
        type: 'GET',
        url: this.event.dataUrl,
        dataType: 'json',
        success: (event) => {
          // Do not potentially overwrite initial date range:
          const { dateStart, dateEnd, dateStartMoment, dateEndMoment, timeStartMoment, timeEndMoment, ...restOfEvent } =
            transformRawEvent(event)
          this.event = { ...this.event, ...restOfEvent }
          this.hasLoaded = true
        },
      }).always(() => {
        this.loading = false
      })
    },

    enlargePhoto() {
      const gallery = new PhotoSwipe({
        index: 0,
        bgOpacity: 1,
        dataSource: [
          {
            src: window.APP.context.site.files_url + this.event.photo.path,
            w: this.event.photo.width,
            h: this.event.photo.height,
          },
        ],
      })
      gallery.init()
    },

    emailUrl(url) {
      return `mailto:${url}`
    },

    externalUrl(url) {
      return externalUrl(url)
    },
  },

  created() {
    this.dataXhr = null
  },

  // language=HTML
  template: `
    <div class="calendar__list-event" :class="{ 'is-cancelled': event.cancelled }">
      <div class="calendar__list-event-header">
        <button
          v-if="hasDetails"
          type="button"
          class="calendar__list-event-header__date"
          aria-label="Toggle details"
          @click="toggle"
        >
          <div class="start date">
            <div class="month">{{ event.dateStartMoment.format('MMM') }}</div>
            <div class="day">{{ event.dateStartMoment.format('D') }}</div>
            <div v-if="!isThisYear" class="year">{{ event.dateStartMoment.format('YYYY') }}</div>
          </div>
          <div class="end date" v-if="event.dateStart !== event.dateEnd">
            <div class="month">{{ event.dateEndMoment.format('MMM') }}</div>
            <div class="day">{{ event.dateEndMoment.format('D') }}</div>
          </div>
        </button>
        <div v-else class="calendar__list-event-header__date">
          <div class="start date">
            <div class="month">{{ event.dateStartMoment.format('MMM') }}</div>
            <div class="day">{{ event.dateStartMoment.format('D') }}</div>
            <div v-if="!isThisYear" class="year">{{ event.dateStartMoment.format('YYYY') }}</div>
          </div>
          <div class="end date" v-if="event.dateStart !== event.dateEnd">
            <div class="month">{{ event.dateEndMoment.format('MMM') }}</div>
            <div class="day">{{ event.dateEndMoment.format('D') }}</div>
          </div>
        </div>
        <div class="calendar__list-event-header__info">
          <button
            v-if="hasDetails"
            type="button"
            class="calendar__list-event-header__photo"
            aria-label="Toggle details"
            @click="toggle"
          >
            <img v-if="thumb" :src="thumb" alt="">
            <template v-else>${placeholderIcon}</template>
          </button>
          <div v-else class="calendar__list-event-header__photo">
            <img v-if="thumb" :src="thumb" alt="">
            <template v-else>${placeholderIcon}</template>
          </div>
          <div class="calendar__list-event-header__title-and-details">
            <div v-if="event.organization" class="calendar__list-event-header__organization">
              {{ event.organization }}
            </div>
            <div class="calendar__list-event-header__title">
              {{ event.title }}
              <div v-if="event.cancelled" class="calendar__list-event-header__cancelled">Cancelled!</div>
            </div>
            <div class="calendar__list-event-header__details">
              <div v-if="event.location">
                {{ event.location }}
              </div>
              <div v-if="event.timeStartMoment">
                {{ event.timeStartMoment.format('h:mma') }}
                <template v-if="event.timeEndMoment">
                  – {{ event.timeEndMoment.format('h:mma') }}
                </template>
              </div>
              <div v-if="event.url">
                <a :href="externalUrl(event.url)" target="_blank">Event Link</a>
                <span class="material-icons" aria-hidden="true">open_in_new</span>
              </div>
            </div>
            <button
              v-if="hasDetails"
              type="button"
              class="calendar__list-event-header__details-button"
              @click="toggle"
              aria-label="Toggle details"
            >
              <template v-if="expanded">
                ${svgIconHtml(collapseIcon)}
                Hide Details
              </template>
              <template v-else>
                ${svgIconHtml(expandIcon)}
                Show Details
              </template>
            </button>
          </div>
          <div v-if="event.categories.length" class="calendar__list-event-header__categories">
            <div
              v-for="category in event.categories"
              :key="category.name"
              class="calendar__list-event-header__category"
            >
              {{ category.name }}
            </div>
          </div>
        </div>
      </div>

      <div class="calendar__list-event-details" v-if="expanded">
        <div class="calendar__list-event-loading" v-if="loading">
          <ui-progress-circular :size="36" :indeterminate="true" />
        </div>
        <div class="event" v-if="event && hasLoaded">
          <div class="event__details">
            <div v-if="event.cancelled" class="alert callout">This event has been cancelled!</div>

            <div v-if="event.photo" class="photo">
              <img :src="event.photo.url" alt="" @click="enlargePhoto">
            </div>

            <!--
            <div class="event__date">
              {{ event.dateStartMoment.format('MMMM Do') }}
              <span v-if="event.dateEndMoment && event.dateStart !== event.dateEnd">
                – {{ event.dateEndMoment.format('MMMM Do') }}
              </span>
              <template v-if="event.timeStartMoment">
                at {{ event.timeStartMoment.format('h:mma') }}
                <template v-if="event.timeEndMoment && event.timeStart !== event.timeEnd">
                  – {{ event.timeEndMoment.format('h:mma') }}
                </template>
              </template>
            </div>
            -->

            <table class="transparent">
              <tbody>
                <tr v-if="event.county">
                  <th scope="row">County:</th>
                  <td>{{ event.county }}</td>
                </tr>
                <tr v-if="event.admission">
                  <th scope="row">Admission:</th>
                  <td>{{ event.admission }}</td>
                </tr>
                <tr v-if="event.location">
                  <th scope="row">Location:</th>
                  <td>{{ event.location }}</td>
                </tr>
                <tr v-if="event.address">
                  <th scope="row">Address:</th>
                  <td>
                    <nl2br tag="span" :text="event.address" />
                  </td>
                </tr>
                <tr v-if="event.contactName || event.contactPhone || event.contactEmail">
                  <th scope="row">Contact:</th>
                  <td>
                    <div v-if="event.contactName">{{ event.contactName }}</div>
                    <div v-if="event.contactPhone">{{ event.contactPhone }}</div>
                    <div v-if="event.contactEmail">
                      <a :href="emailUrl(event.contactEmail)" target="_blank">Click here to email</a>
                      <span class="material-icons" aria-hidden="true">open_in_new</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <div v-if="event.richDescription" class="description" v-html="event.richDescription"></div>
            <div v-else-if="event.textDescription" class="description" v-html="event.textDescription"></div>

            <div class="event__link" v-if="event.url">
              <a class="button" :href="externalUrl(event.url)" target="_blank">
                <span>More Information</span>
                <span class="material-icons" aria-hidden="true" style="vertical-align: top">open_in_new</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
}
